import {
  circle,
  ellipse,
  fill,
  flat,
  INK,
  line,
  P,
  poly,
  polyline,
  rect,
  shine,
  type IconDef,
  type Shape,
} from '../kit';

const r2 = (v: number): number => Math.round(v * 100) / 100;

/** One shape per (x, y) pair in `pts`, joined into a single path. */
const each = (pts: readonly number[], f: (x: number, y: number) => string): string => {
  let d = '';
  for (let i = 0; i + 1 < pts.length; i += 2) d += f(pts[i] as number, pts[i + 1] as number);
  return d;
};

/** Sends every point of absolute path data (M, L, C, Q and Z only) through `f`. */
const mapPts = (d: string, f: (x: number, y: number) => readonly [number, number]): string =>
  d.replace(/(-?\d*\.?\d+)[ ,]+(-?\d*\.?\d+)/g, (_m, x: string, y: string) => {
    const [a, b] = f(Number(x), Number(y));
    return `${r2(a)} ${r2(b)}`;
  });

/** Where (x, y) lands when turned `deg` degrees clockwise about the middle of the box, then moved. */
const at = (x: number, y: number, deg: number, dx = 0, dy = 0): [number, number] => {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [16 + (x - 16) * c - (y - 16) * s + dx, 16 + (x - 16) * s + (y - 16) * c + dy];
};

/** Turns absolute path data (M, L, C, Q, Z) `deg` degrees clockwise about the middle, then moves it. */
const turn = (d: string, deg: number, dx = 0, dy = 0): string => mapPts(d, (x, y) => at(x, y, deg, dx, dy));

/** Mirrors absolute path data (M, L, C, Q, Z) left to right across the middle of the box. */
const flip = (d: string): string => mapPts(d, (x, y) => [32 - x, y]);

/** A circle wound the other way round from `circle`, to punch a hole in a round shape. */
const hole = (cx: number, cy: number, r: number): string =>
  `M${r2(cx - r)} ${r2(cy)}a${r} ${r} 0 1 1 ${r2(2 * r)} 0a${r} ${r} 0 1 1 ${r2(-2 * r)} 0Z`;

/** An ellipse drawn with curves, so `turn` can rotate it. */
const oval = (cx: number, cy: number, rx: number, ry: number): string => {
  const kx = rx * 0.5523;
  const ky = ry * 0.5523;
  const p = (x: number, y: number): string => `${r2(x)} ${r2(y)}`;
  return (
    `M${p(cx - rx, cy)}C${p(cx - rx, cy - ky)} ${p(cx - kx, cy - ry)} ${p(cx, cy - ry)}` +
    `C${p(cx + kx, cy - ry)} ${p(cx + rx, cy - ky)} ${p(cx + rx, cy)}` +
    `C${p(cx + rx, cy + ky)} ${p(cx + kx, cy + ry)} ${p(cx, cy + ry)}` +
    `C${p(cx - kx, cy + ry)} ${p(cx - rx, cy + ky)} ${p(cx - rx, cy)}Z`
  );
};

/** A thick stroke of `color` with the ink outline around it (handles, tubes, cords). */
const band = (d: string, w: number, color: string): Shape[] => [line(d, w + 1.6, INK), line(d, w, color)];

/** A rectangle `len` long and `wid` wide centered on (cx, cy), its length turned `deg` degrees clockwise. */
const box = (cx: number, cy: number, len: number, wid: number, deg: number): string => {
  const a = (deg * Math.PI) / 180;
  const ux = (Math.cos(a) * len) / 2;
  const uy = (Math.sin(a) * len) / 2;
  const vx = (-Math.sin(a) * wid) / 2;
  const vy = (Math.cos(a) * wid) / 2;
  return poly(
    cx - ux - vx,
    cy - uy - vy,
    cx + ux - vx,
    cy + uy - vy,
    cx + ux + vx,
    cy + uy + vy,
    cx - ux + vx,
    cy - uy + vy,
  );
};

/** A four-point sparkle. */
const sparkle = (cx: number, cy: number, r: number): string => {
  const k = r * 0.2;
  return (
    `M${r2(cx)} ${r2(cy - r)}Q${r2(cx + k)} ${r2(cy - k)} ${r2(cx + r)} ${r2(cy)}` +
    `Q${r2(cx + k)} ${r2(cy + k)} ${r2(cx)} ${r2(cy + r)}Q${r2(cx - k)} ${r2(cy + k)} ${r2(cx - r)} ${r2(cy)}` +
    `Q${r2(cx - k)} ${r2(cy - k)} ${r2(cx)} ${r2(cy - r)}Z`
  );
};

/** A triangular arrowhead with its tip at (tx, ty), pointing away from (fx, fy). */
const head = (fx: number, fy: number, tx: number, ty: number, len: number, half: number): string => {
  const l = Math.hypot(tx - fx, ty - fy);
  const ux = (tx - fx) / l;
  const uy = (ty - fy) / l;
  const bx = tx - ux * len;
  const by = ty - uy * len;
  return poly(tx, ty, bx - uy * half, by + ux * half, bx + uy * half, by - ux * half);
};

/** A coiled cord from (x0, y0) to (x1, y1): `n` loops, `rx` wide and `ry` deep. */
function coil(x0: number, y0: number, x1: number, y1: number, n: number, rx: number, ry: number): string {
  const len = Math.hypot(x1 - x0, y1 - y0);
  const ux = (x1 - x0) / len;
  const uy = (y1 - y0) / len;
  const step = len / (n * 2 * Math.PI);
  const pts: number[] = [];
  for (let i = 0; i <= n * 12; i++) {
    const a = (i / 12) * 2 * Math.PI;
    const along = step * a - rx * Math.sin(a);
    const across = ry * (1 - Math.cos(a));
    pts.push(x0 + ux * along + uy * across, y0 + uy * along - ux * across);
  }
  return polyline(...pts);
}

/** A point on the DNA helix, `t` from 0 (bottom) to 1 (top); `phase` 0 or 1 picks the strand. */
function helixPt(t: number, phase: number): [number, number] {
  const along = -12.4 + 24.8 * t;
  const across = 5.4 * Math.sin(t * 3 * Math.PI) * (phase ? -1 : 1);
  return [16 + 0.42 * along + 0.91 * across, 16 - 0.91 * along + 0.42 * across];
}

/** The half-twists of one DNA strand (`phase` 0 or 1) that pass in front of the other, or behind it. */
function strand(phase: number, front: boolean): string {
  let d = '';
  for (let k = 0; k < 3; k++) {
    if ((k % 2 === phase) !== front) continue;
    const pts: number[] = [];
    for (let i = 0; i <= 12; i++)
      pts.push(...helixPt(Math.min(1, Math.max(0, (k - 0.02 + (i / 12) * 1.04) / 3)), phase));
    d += polyline(...pts);
  }
  return d;
}

/** The rungs between the two DNA strands. */
function rungs(): string {
  let d = '';
  for (let k = 0; k < 3; k++)
    for (const f of [0.25, 0.5, 0.75]) {
      const [ax, ay] = helixPt((k + f) / 3, 0);
      const [bx, by] = helixPt((k + f) / 3, 1);
      d += `M${r2(ax)} ${r2(ay)}L${r2(bx)} ${r2(by)}`;
    }
  return d;
}

/** The keys of a keyboard: three rows of six and a space bar row. */
function keys(): string {
  let d = '';
  const w = 3.2;
  const g = 0.9;
  for (let row = 0; row < 3; row++)
    for (let i = 0; i < 6; i++) d += rect(4.15 + i * (w + g), 10.4 + row * 3.3, w, 2.4, 0.5);
  d += rect(4.15, 20.3, w, 2.4, 0.5) + rect(4.15 + w + g, 20.3, 4 * w + 3 * g, 2.4, 0.5);
  d += rect(4.15 + 5 * (w + g), 20.3, w, 2.4, 0.5);
  return d;
}

/** The telephone handset, lying flat with its ear and mouth cups facing down. */
const HANDSET =
  'M5.2 14.6 L5.6 9.8 C5.8 7.8 7.4 6.6 9.4 6.6 C13 5.6 19 5.6 22.6 6.6 C24.6 6.6 26.2 7.8 26.4 9.8 ' +
  'L26.8 14.6 C26.9 15.6 26.2 16.4 25.2 16.4 L21.2 16.4 C20.2 16.4 19.5 15.6 19.6 14.6 L19.9 12 ' +
  'C17.4 11.2 14.6 11.2 12.1 12 L12.4 14.6 C12.5 15.6 11.8 16.4 10.8 16.4 L6.8 16.4 ' +
  'C5.8 16.4 5.1 15.6 5.2 14.6 Z';

/** A price tag lying flat, pointy end left. */
const TAG =
  'M4.5 16 L10.5 9.5 L25.5 9.5 C26.6 9.5 27.5 10.4 27.5 11.5 L27.5 20.5 C27.5 21.6 26.6 22.5 25.5 22.5 L10.5 22.5 Z';

/** The gas-station shades in one piece: top bar, both lenses and the bridge. */
const SHADES =
  'M3.2 10.2 L28.8 10.2 C29.3 10.2 29.5 10.7 29.4 11.2 L28.2 16.8 C27.6 19.5 25.4 21.2 22.6 21.2 ' +
  'C19.6 21.2 17.8 19.4 17.4 16.6 L17 13.8 C16.4 13.2 15.6 13.2 15 13.8 L14.6 16.6 ' +
  'C14.2 19.4 12.4 21.2 9.4 21.2 C6.6 21.2 4.4 19.5 3.8 16.8 L2.6 11.2 C2.5 10.7 2.7 10.2 3.2 10.2 Z';

/** The tinted left lens, inside the frame. */
const LENS =
  'M4.6 12.6L13.6 12.6C14 12.6 14.2 12.9 14.1 13.3L13.6 16.4C13.2 18.8 11.6 20.1 9.4 20.1C7.2 20.1 5.6 18.9 5.1 16.6L4.3 13.4C4.2 12.9 4.3 12.6 4.6 12.6Z';

/** A pan of the balance scales (the left one). */
const PAN = 'M2.8 19 L9.2 19 C9.2 21.3 7.8 22.8 6 22.8 C4.2 22.8 2.8 21.3 2.8 19 Z';

/** The Croc, seen from above and to the side, toe to the right: its upper, sole, heel strap and holes. */
const CROC =
  'M8.2 23.2C6.6 22.4 5.8 20.6 6 18.6C6.2 16.2 8 14.4 10.8 14.2C13.6 14 16 14 17.8 12.6C20.8 10.2 26.6 9.8 28.9 12.8C30.4 14.8 30.4 19.6 29 21.8C28.2 23 27 23.2 25.6 23.2Z';
const CROC_SOLE = 'M6.2 20.8C6.2 24 8 25.6 10.6 25.6L26.6 25.6C29 25.6 30.2 23.8 30 21Z';
const CROC_STRAP = 'M15.4 21C12 21.4 8.6 21.2 7 19.6C5.8 18.4 6 16.2 7.8 15';
const CROC_HOLES = each(
  [21.4, 13, 24, 12.6, 26.6, 13.2, 21.2, 15.9, 23.9, 15.7, 26.7, 16.1, 22.2, 18.8, 24.9, 18.8, 27.6, 19],
  (x, y) => oval(x, y, 1.05, 0.8),
);

/** Tips the Croc back mid-stride (toe up), a touch smaller, so the motion lines fit behind it. */
const stride = (d: string): string =>
  mapPts(d, (x, y) => {
    const [a, b] = at(x, y, -10);
    return [16 + (a - 16) * 0.88 + 0.1, 16 + (b - 16) * 0.88 - 1.1];
  });

export const stuff = {
  /** The player's phone: a smartphone with a cracked screen. */
  phone: [
    fill(P.charcoal, rect(8.5, 2.5, 15, 27, 3.2)),
    flat(P.sky, rect(10.3, 5.4, 11.4, 19.4, 0.8)),
    shine(poly(10.9, 6, 15.6, 6, 10.9, 12.6), 0.45),
    line(
      'M17.8 10.4L20.6 6.4M17.8 10.4L14.6 11L11.2 9.2M17.8 10.4L16.4 14.6L18.8 17.8L15.4 21.6L16.8 24.2M17.8 10.4L21 13.2',
      1.2,
    ),
    flat(P.gray, circle(16, 27.1, 1.1)),
  ],
  /** A black telephone handset (payphone receiver) with a coiled cord. */
  telephone: [
    line(coil(16.7, 18.6, 3.9, 26.3, 4.5, 1.3, 1.4), 1.6),
    fill(P.charcoal, turn(HANDSET, 40, -1.7, -0.3)),
    line(turn('M7.6 10.4C7.8 8.8 8.8 8 10.4 7.8C13.4 7 18.6 7 21.6 7.8', 40, -1.7, -0.3), 1.3, P.white, 0.45),
  ],
  /** A camera with a flash. */
  camera: [
    fill(P.charcoal, rect(8, 5.5, 9.5, 5.5, 1.6)),
    fill(P.red, rect(21.5, 6.8, 4, 3, 0.8), { w: 1.2 }),
    fill(P.charcoal, rect(3, 9, 26, 18.5, 3)),
    fill(P.silver, circle(16, 18.2, 7.2)),
    fill(P.navy, circle(16, 18.2, 4.4)),
    shine(circle(14.3, 16.5, 1.4), 0.75),
    fill(P.yellow, rect(22.4, 11.4, 4.4, 2.8, 0.6), { w: 1.2 }),
  ],
  /** A handheld camcorder with a red record light. */
  'video-camera': [
    fill(P.charcoal, rect(13, 6.8, 11, 5, 1.6)),
    fill(P.charcoal, rect(10, 10.2, 19, 15, 3)),
    fill(P.black, 'M11 13.8L4 11.6V23.8L11 21.6Z'),
    fill(P.blue, ellipse(4, 17.7, 1.4, 6)),
    fill(P.sky, rect(14, 14.2, 9, 6.5, 1), { w: 1.2 }),
    fill(P.red, circle(26, 13.6, 1.6), { w: 1.2 }),
  ],
  /** A framed picture: gold frame, a little landscape. */
  picture: [
    fill(P.gold, rect(3, 5, 26, 22, 1.6)),
    flat(P.sky, rect(6.6, 8.6, 18.8, 14.8)),
    flat(P.yellow, circle(20.6, 12.6, 2.2)),
    flat(P.green, 'M6.6 23.4L6.6 18.2C9 15.4 12.2 15 15.2 17.6C17.8 15.2 21.8 14.6 25.4 17.8L25.4 23.4Z'),
    flat(P.grass, 'M6.6 23.4L6.6 20.6C10 18.6 14 18.8 17 21C19.6 19.8 23 19.8 25.4 21.2L25.4 23.4Z'),
    line(rect(6.6, 8.6, 18.8, 14.8), 1.4),
  ],
  /** A clipboard with a paper checklist. */
  clipboard: [
    fill(P.wood, rect(5.5, 4.5, 21, 25, 2)),
    fill(P.white, rect(8, 8, 16, 19, 0.8), { w: 1.2 }),
    fill(P.silver, rect(11, 2.6, 10, 5.4, 1.6) + circle(16, 4.6, 1.1)),
    line(polyline(10.2, 12.6, 11.6, 14, 14, 11.2) + polyline(10.2, 18.1, 11.6, 19.5, 14, 16.7), 1.5, P.green),
    line('M16 12.8H21.6M16 18.3H21.6M16 23.8H20', 1.5, P.gray),
    fill(P.white, rect(10.2, 22.4, 2.8, 2.8, 0.4), { w: 1.1 }),
  ],
  /** A rolled paper scroll, partly open. */
  scroll: [
    fill(P.paper, rect(7, 7, 18, 18)),
    line('M10 12.4H22M10 15.8H20M10 19.2H21', 1.4, P.wood),
    fill(P.beige, rect(4, 3.5, 24, 6, 3)),
    line(circle(7, 6.5, 1.4), 1.2),
    fill(P.beige, rect(4, 22.5, 24, 6, 3)),
    line(circle(25, 25.5, 1.4), 1.2),
  ],
  /** A folded newspaper. */
  newspaper: [
    fill(P.lightGray, rect(19.5, 8.5, 9, 19.5, 2)),
    fill(P.white, rect(3.5, 4.5, 21, 23.5, 1.4)),
    flat(P.charcoal, rect(6, 7.2, 16, 3.2, 0.4)),
    flat(P.sky, rect(6, 12.8, 7, 6.4, 0.4)),
    line('M15.4 13.4H21.8M15.4 16H21.8M15.4 18.6H21.8M6 22.2H21.8M6 24.9H18', 1.3, P.gray),
  ],
  /** A chart with a green line going up. */
  'chart-up': [
    fill(P.white, rect(4, 4, 24, 24, 2.6)),
    line('M5.6 11H26.4M5.6 17H26.4M5.6 23H26.4', 1, P.lightGray),
    line(polyline(8, 23, 13, 16.6, 16.8, 19.6, 22.4, 11.2), 2.4, P.green),
    flat(P.green, head(16.8, 19.6, 24.1, 8.7, 4.6, 2.9)),
  ],
  /** A bar chart with three colored bars. */
  'bar-chart': [
    fill(P.white, rect(4, 4, 24, 24, 2.6)),
    fill(P.red, rect(7.6, 14.5, 4.6, 10, 0.6), { w: 1.2 }),
    fill(P.gold, rect(13.7, 8.5, 4.6, 16, 0.6), { w: 1.2 }),
    fill(P.blue, rect(19.8, 12, 4.6, 12.5, 0.6), { w: 1.2 }),
    line('M6.6 24.5H25.4', 1.4),
  ],
  /** A tear-off calendar page with a red top. */
  calendar: [
    fill(P.white, rect(4, 6, 24, 23, 2.6)),
    fill(P.red, 'M4 13L4 8.6C4 7.16 5.16 6 6.6 6L25.4 6C26.84 6 28 7.16 28 8.6L28 13Z'),
    fill(P.silver, rect(9.4, 3, 2.2, 6, 1.1), { w: 1.2 }),
    fill(P.silver, rect(20.4, 3, 2.2, 6, 1.1), { w: 1.2 }),
    flat(
      P.lightGray,
      each(
        [
          6.5, 15.2, 11.7, 15.2, 16.9, 15.2, 22.1, 15.2, 6.5, 19.6, 11.7, 19.6, 22.1, 19.6, 6.5, 24, 11.7, 24,
          16.9, 24, 22.1, 24,
        ],
        (x, y) => rect(x, y, 3.4, 3.4, 0.5),
      ),
    ),
    flat(P.red, rect(16.9, 19.6, 3.4, 3.4, 0.5)),
  ],
  /** A red map pin. */
  pin: [
    fill(
      P.red,
      'M16 29.5C13 25.8 6 19.6 6 12.6C6 7 10.5 2.6 16 2.6C21.5 2.6 26 7 26 12.6C26 19.6 19 25.8 16 29.5Z',
    ),
    fill(P.white, circle(16, 12.4, 3.9)),
    shine(ellipse(10.4, 10, 1.3, 2.4), 0.45),
  ],
  /** A red pushpin. */
  pushpin: [
    ...band(turn('M16 19.4L16 29.6', 40, -1.6, 1), 1.2, P.silver),
    fill(P.red, turn(oval(16, 16.6, 8.4, 2.8), 40, -1.6, 1)),
    fill(P.red, turn('M12.4 6.2L13.4 15.2L18.6 15.2L19.6 6.2Z', 40, -1.6, 1)),
    fill(P.red, turn(oval(16, 5.6, 7, 3), 40, -1.6, 1)),
    shine(turn(oval(13.6, 4.8, 2.4, 1), 40, -1.6, 1), 0.5),
  ],
  /** A rural mailbox on a post with its red flag up. */
  mailbox: [
    fill(P.wood, rect(13.8, 19.5, 4.4, 10, 0.6)),
    fill(P.silver, 'M8.5 7.5H23.5C26.26 7.5 28.5 9.74 28.5 12.5V20.5H8.5Z'),
    fill(P.red, 'M22.4 17V3.4H28.4V7.6H24.6V17Z'),
    fill(P.lightGray, 'M3.5 20.5V12.5C3.5 9.74 5.74 7.5 8.5 7.5C11.26 7.5 13.5 9.74 13.5 12.5V20.5Z'),
    fill(P.gray, rect(7.4, 13, 2.2, 3.2, 0.8), { w: 1.2 }),
  ],
  /** A white envelope. */
  envelope: [
    fill(P.white, rect(3.5, 7, 25, 18, 2.2)),
    flat(P.cream, 'M4.7 8L16 17L27.3 8Z'),
    line('M4.6 23.8L12.8 16.6M27.4 23.8L19.2 16.6', 1.3),
    line(polyline(4.4, 8.4, 16, 17.6, 27.6, 8.4), 1.5),
  ],
  /** A paper price tag on a string. */
  'price-tag': [
    fill(P.beige, turn(TAG, 45, -1.7)),
    line(turn('M14.5 13.6L23.5 13.6M14.5 18.4L21 18.4', 45, -1.7), 1.5, P.brown),
    fill(P.white, circle(...at(10.6, 16, 45, -1.7), 1.7), { w: 1.2 }),
    line('M10.5 12.2C9.3 6.5 13.3 3.4 17.7 4.4C20.9 5.2 20.7 9 18.1 9.2', 1.5, P.red),
  ],
  /** A long paper receipt with a zigzag bottom. */
  receipt: [
    fill(
      P.white,
      'M7.5 3H24.5V27.4L22.38 29.4L20.25 27.4L18.13 29.4L16 27.4L13.88 29.4L11.75 27.4L9.63 29.4L7.5 27.4Z',
    ),
    line('M10.5 7.6H21.5M10.5 11.2H18.5M10.5 14.8H21.5M10.5 18.4H17', 1.4, P.gray),
    line('M10.5 23H21.5', 1.8),
  ],
  /** A payment card: green with a gold chip. */
  'credit-card': [
    fill(P.green, rect(3, 7, 26, 18, 2.6)),
    flat(P.darkGreen, rect(3.8, 10.2, 24.4, 3.4)),
    fill(P.gold, rect(6.6, 15.4, 5.6, 4.4, 1), { w: 1.2 }),
    line('M6.6 22H10.6M13 22H17M19.4 22H23.4', 1.6, P.lemon),
  ],
  /** A brown leather briefcase. */
  briefcase: [
    ...band('M12.4 10L12.4 8C12.4 6.9 13.3 6 14.4 6L17.6 6C18.7 6 19.6 6.9 19.6 8L19.6 10', 2, P.darkBrown),
    fill(P.wood, rect(3.5, 9.5, 25, 18, 2.6)),
    line('M4.3 16H27.7', 1.3),
    fill(P.gold, rect(14, 14.2, 4, 3.8, 0.8), { w: 1.2 }),
  ],
  /** A gold padlock, closed. */
  lock: [
    ...band('M10.6 15L10.6 10.4C10.6 7.42 13.02 5 16 5C18.98 5 21.4 7.42 21.4 10.4L21.4 15', 2.6, P.silver),
    fill(P.gold, rect(6.5, 14, 19, 15, 3)),
    flat(P.darkBrown, circle(16, 19.7, 2) + 'M14.8 20.4L17.2 20.4L17.9 25L14.1 25Z'),
    shine(rect(8.6, 16, 1.8, 9, 0.9), 0.4),
  ],
  /** A magnifying glass. */
  magnifier: [
    ...band('M21 21L27.2 27.2', 3.6, P.brown),
    fill(P.charcoal, circle(13.5, 13.5, 10)),
    fill(P.glass, circle(13.5, 13.5, 7.2)),
    shine(ellipse(10.6, 10.6, 2, 1.4), 0.7),
  ],
  /** A rifle scope: black tube, crosshair lens. */
  scope: [
    ...band('M22.6 9.4L25 7', 5.8, P.charcoal),
    fill(P.charcoal, rect(16.6, 5.4, 3.8, 5.2, 0.8)),
    fill(P.charcoal, rect(19.4, 13.2, 5.2, 3.6, 0.8)),
    ...band('M14.4 17.6L22.8 9.2', 4, P.charcoal),
    fill(P.charcoal, circle(11, 20.6, 8.4)),
    fill(P.sky, circle(11, 20.6, 5.8), { w: 2.6 }),
    line('M11 15.6V25.6M6 20.6H16', 1.2),
    flat(P.red, circle(11, 20.6, 1)),
    shine(ellipse(8.2, 17.8, 1.2, 0.8), 0.6),
  ],
  /** A purple crystal ball on a gold stand, swirly glow. */
  'crystal-ball': [
    fill(P.gold, 'M8.5 28.6C8.5 25.8 10 23.6 12.2 23L19.8 23C22 23.6 23.5 25.8 23.5 28.6Z'),
    fill(P.purple, circle(16, 13.8, 10.6)),
    line('M10.4 15.8C10.4 10.8 16.6 8.8 19.4 12.2C21.4 14.8 18.6 18 15.6 16.4', 1.8, P.lavender),
    shine(ellipse(11.4, 8.8, 2.6, 1.6), 0.6),
    flat(P.lavender, sparkle(27, 4.6, 2.4) + sparkle(4.8, 7.4, 1.7)),
  ],
  /** Save: a floppy disk. */
  save: [
    fill(
      P.blue,
      'M4 6.6C4 5.16 5.16 4 6.6 4L23.4 4L28 8.6L28 25.4C28 26.84 26.84 28 25.4 28L6.6 28C5.16 28 4 26.84 4 25.4Z',
    ),
    fill(P.silver, rect(9.6, 4, 12, 8, 0.8), { w: 1.3 }),
    flat(P.blue, rect(17.2, 5.6, 2.6, 4.8, 0.4)),
    fill(P.white, rect(8, 16.4, 16, 11.6, 1), { w: 1.3 }),
    line('M11 20.4H21M11 23.8H18', 1.3, P.gray),
  ],
  /** A computer keyboard. */
  keyboard: [fill(P.charcoal, rect(2.5, 8.5, 27, 15.5, 2.6)), flat(P.silver, keys())],
  /** A speaker with sound waves. */
  speaker: [
    fill(P.charcoal, rect(3.5, 11.6, 6.4, 8.8, 1.2)),
    fill(P.silver, 'M9.4 11.6L16.6 5.6L16.6 26.4L9.4 20.4Z'),
    ...band('M20.4 12.2C22.4 14.4 22.4 17.6 20.4 19.8', 2.2, P.sky),
    ...band('M24 8.6C27.8 12.8 27.8 19.2 24 23.4', 2.2, P.sky),
  ],
  /** Cell signal bars with one bar of signal (the first, green); the rest gray. Lucky Acres gets one bar. */
  signal: [
    fill(P.green, rect(3.6, 20, 5, 7, 1)),
    fill(P.lightGray, rect(10.2, 15, 5, 12, 1)),
    fill(P.lightGray, rect(16.8, 10, 5, 17, 1)),
    fill(P.lightGray, rect(23.4, 5, 5, 22, 1)),
  ],
  /** Share: a box with an arrow coming out of the top. */
  share: [
    ...band(
      'M11 13.1L8 13.1C6.6 13.1 5.6 14.1 5.6 15.5L5.6 25.5C5.6 26.9 6.6 27.9 8 27.9L24 27.9C25.4 27.9 26.4 26.9 26.4 25.5L26.4 15.5C26.4 14.1 25.4 13.1 24 13.1L21 13.1',
      2.6,
      P.blue,
    ),
    fill(
      P.orange,
      'M13.8 21.1L13.8 10.9L9.8 10.9L16 3.1L22.2 10.9L18.2 10.9L18.2 21.1C18.2 22.3 17.2 23.1 16 23.1C14.8 23.1 13.8 22.3 13.8 21.1Z',
    ),
  ],
  /** The Earth: a blue and green globe. */
  globe: [
    fill(P.blue, circle(16, 16, 13)),
    flat(
      P.grass,
      'M6.5 10.5C8 7.5 12 6.5 14 8.5C15 9.6 13.6 11.4 12.6 12.4C11.8 13.2 12.8 14.6 14 15.4C15.6 16.4 15.4 18.6 14.4 20.4C13.4 22.2 13 24.4 11.6 25.4C10.8 23.8 10.6 21.6 10 19.8C9.4 18 7.4 17.6 6.4 16C5.6 14.6 5.6 12.2 6.5 10.5Z',
    ),
    flat(
      P.grass,
      'M18.5 8C20.5 6.8 24 7.6 25.6 9.6C26.6 11 25.2 12.2 23.8 12.4C25.4 13.4 26.8 14.6 26.4 16.8C26 19 24.2 20.2 23.4 22.2C22.8 23.6 21.4 24.4 20.6 23.2C19.8 21.8 20.4 19.8 19.4 18.4C18.4 17 17.6 15.6 18.4 14C19 12.8 17.4 12.2 17.2 10.8C17 9.6 17.6 8.6 18.5 8Z',
    ),
    shine(ellipse(10.5, 7.5, 2.6, 1.4), 0.5),
  ],
  /** A DNA double helix (paternity test!). */
  dna: [
    ...band(strand(0, false), 2, P.blue),
    ...band(strand(1, false), 2, P.hotPink),
    ...band(rungs(), 1.3, P.gold),
    ...band(strand(0, true), 2, P.blue),
    ...band(strand(1, true), 2, P.hotPink),
  ],
  /** A thermometer with red mercury. */
  thermometer: [
    fill(P.white, 'M11.8 19.76V7A3.5 3.5 0 0 1 18.8 7V19.76A5.5 5.5 0 1 1 11.8 19.76Z'),
    flat(
      P.red,
      'M13.7 21V11.6C13.7 10.7 14.4 10 15.3 10C16.2 10 16.9 10.7 16.9 11.6V21A3.8 3.8 0 1 1 13.7 21Z',
    ),
    shine(ellipse(13.9, 23, 0.9, 1.3), 0.55),
    line('M20.1 9.4H22.3M20.1 12.8H22.3M20.1 16.2H22.3', 1.4),
  ],
  /** Balance scales. */
  balance: [
    line('M6 9.6L3.2 19.2M6 9.6L8.8 19.2M26 9.6L23.2 19.2M26 9.6L28.8 19.2', 1.2),
    fill(P.gold, 'M9.5 29C9.5 26.6 12.2 25.4 16 25.4C19.8 25.4 22.5 26.6 22.5 29Z'),
    fill(P.gold, rect(14.6, 8, 2.8, 18)),
    fill(P.gold, rect(4.4, 7.8, 23.2, 2.6, 1.3)),
    fill(P.gold, circle(16, 6.6, 2.2)),
    fill(P.gold, PAN),
    fill(P.gold, flip(PAN)),
  ],
  /** A wooden plank/board. */
  plank: [
    fill(P.wood, turn(poly(3.4, 19, 28.6, 19, 28.6, 21.5, 3.4, 21.5), -32)),
    fill(P.tan, turn(poly(3.4, 11, 28.6, 11, 28.6, 19, 3.4, 19), -32)),
    line(
      turn(
        'M5.6 13.6C9 12.8 12 14.4 16 13.6C19 13 21.5 14.2 24.6 13.4M8.4 16.6C12 15.8 15.6 17.2 20 16.4C23 15.9 25 16.8 27 16.2',
        -32,
      ),
      1.2,
      P.wood,
    ),
    line(turn(oval(20.5, 15.2, 1.6, 0.9), -32), 1.2, P.wood),
    flat(P.gray, circle(...at(5.8, 15, -32), 0.9) + circle(...at(26.2, 15, -32), 0.9)),
  ],
  /** A banjo: round drum, long neck, tuning pegs. */
  banjo: [
    ...band('M13.6 18.4L24 8', 3, P.wood),
    fill(
      P.cream,
      each([28.22, 7.46, 24.54, 3.78, 26.66, 9.02, 22.98, 5.34], (x, y) => circle(x, y, 1.1)),
      { w: 1.1 },
    ),
    fill(P.darkBrown, box(25.6, 6.4, 5, 4, -45)),
    fill(P.silver, circle(11, 21, 8.2)),
    fill(P.cream, circle(11, 21, 6.2)),
    line('M7.2 25.65L24.4 8.45M6.4 24.75L23.6 7.55', 0.8, P.darkGray),
    fill(P.darkBrown, box(9.2, 22.8, 3.6, 1.3, 45), { w: 1 }),
  ],
  /** A white T-shirt. */
  tshirt: [
    fill(
      P.white,
      'M12 4.5C13 6.6 14.4 7.6 16 7.6C17.6 7.6 19 6.6 20 4.5L25.6 6.6L29.4 13L24.8 15.6L23.6 13.2L23.6 28L8.4 28L8.4 13.2L7.2 15.6L2.6 13L6.4 6.6Z',
    ),
    line('M11.6 5.4C12.8 7.8 14.3 8.8 16 8.8C17.7 8.8 19.2 7.8 20.4 5.4', 1.2),
  ],
  /** A pair of blue jeans. */
  jeans: [
    fill(P.denim, 'M7.6 3.5L24.4 3.5L27 28.5L18.6 28.5L16 13.6L13.4 28.5L5 28.5Z'),
    line('M7.3 6.7L24.7 6.7M16 6.7L16 12', 1.3, P.navy),
    line('M9.2 6.8C9.6 9.4 11 10.6 13.2 10.8M22.8 6.8C22.4 9.4 21 10.6 18.8 10.8', 1.3, P.navy),
    line('M5.3 25.6L13.9 25.6M18.1 25.6L26.7 25.6', 1.3, P.navy),
    flat(P.gold, circle(16, 5.2, 0.9)),
  ],
  /** A single white tube sock with stripes. */
  sock: [
    fill(
      P.white,
      'M7 3.5L17.6 3.5L17.6 16L23.6 21.4C25.6 23.2 25.6 26.4 23.6 28C21.8 29.4 19.4 29.2 17.8 27.8L9.2 20.8C7.8 19.6 7 18 7 16.2Z',
    ),
    flat(P.red, rect(7.8, 7, 9, 2.2)),
    flat(P.blue, rect(7.8, 10.6, 9, 2.2)),
  ],
  /** A brown work boot. */
  boot: [
    fill(
      P.wood,
      'M6 4L16.6 4C17.2 4 17.6 4.4 17.6 5L17.6 13.6C17.6 15.2 18.6 16.4 20.2 16.9L25 18.4C27.2 19.1 28.4 21 28.4 23L28.4 25L3.8 25L4.8 15.4Z',
    ),
    fill(P.brown, rect(5.6, 3.4, 12.4, 3.4, 1.4)),
    fill(
      P.darkBrown,
      'M3.4 24.4L28.8 24.4L28.8 26.6C28.8 27.8 27.8 28.8 26.6 28.8L5.6 28.8C4.4 28.8 3.4 27.8 3.4 26.6Z',
    ),
    line('M20.4 17.2C21.6 19.4 21.8 22 21.2 24.4', 1.3),
    line('M14 8.4L17.6 8.4M14.2 11.4L17.6 11.4M14.6 14.4L18 14.8', 1.4, P.cream),
  ],
  /**
   * Crocs in SPORT MODE (the sprint button): a lime-green Croc clog with holes on top, the heel strap
   * flipped back, motion lines.
   */
  croc: [
    line('M2.5 13.4L6.4 12.7M2.4 17.7L5.1 17.2M2.6 22.5L7.6 21.7', 1.6),
    fill(P.green, stride(CROC_SOLE)),
    fill(P.lime, stride(CROC)),
    fill(P.green, stride(oval(12.6, 17.4, 4.9, 2.1))),
    flat(P.darkGreen, stride(CROC_HOLES)),
    ...band(stride(CROC_STRAP), 2.1, P.lime),
    fill(P.lime, stride(oval(15.4, 21, 1.6, 1.6)), { w: 1.2 }),
  ],
  /** A red-and-white trucker cap, side view. */
  cap: [
    fill(P.white, 'M3.5 23C2.9 15.6 7.1 8.6 13.5 8.2C18.5 7.9 21.9 11.4 22.1 17L22.1 23Z'),
    flat(
      P.lightGray,
      each(
        [8.6, 11.8, 10.9, 11.8, 7.4, 14.6, 9.7, 14.6, 6.2, 17.4, 8.5, 17.4, 10.8, 17.4, 7.4, 20.2, 9.7, 20.2],
        (x, y) => circle(x, y, 0.7),
      ),
    ),
    fill(P.red, 'M13.5 8.2C18.5 7.9 21.9 11.4 22.1 17L22.1 23L13.9 23C12.7 18.8 12.5 12.6 13.5 8.2Z'),
    fill(
      P.red,
      'M19.5 21.4C24.1 20.8 27.9 21.6 29.1 23.4C29.5 24.2 28.9 25 27.9 24.8C25.1 24.3 21.9 24.2 19.1 24.6Z',
    ),
    fill(P.red, ellipse(13.5, 7.8, 1.8, 0.9), { w: 1.2 }),
  ],
  /** Cheap gas-station sunglasses (black). */
  shades: [
    fill(P.black, SHADES),
    flat(P.charcoal, LENS + flip(LENS)),
    shine(rect(4.4, 11, 23.2, 0.9, 0.45), 0.3),
    shine(poly(6.6, 13.2, 9, 13.2, 7, 18.4, 5.8, 16), 0.45),
    shine(poly(19.8, 13.2, 22.2, 13.2, 20.2, 18.4, 19, 16), 0.45),
  ],
  /** Reading glasses. */
  glasses: [
    fill(P.glass, ellipse(9.2, 16.6, 6.2, 5.4), { w: 2.2 }),
    fill(P.glass, ellipse(22.8, 16.6, 6.2, 5.4), { w: 2.2 }),
    line('M15.2 15.2C15.6 13.8 16.4 13.8 16.8 15.2', 2),
    line('M5.6 15.4C5.8 13.6 7 12.6 8.6 12.4M19.2 15.4C19.4 13.6 20.6 12.6 22.2 12.4', 1.4, P.white),
  ],
  /** A gold crown with red jewels. */
  crown: [
    fill(P.gold, circle(4.2, 9.2, 1.7) + circle(16, 5.2, 1.7) + circle(27.8, 9.2, 1.7)),
    fill(P.gold, 'M5.6 24L4.2 10.4L10.6 16.4L16 6.4L21.4 16.4L27.8 10.4L26.4 24Z'),
    fill(P.gold, rect(4.6, 22.4, 22.8, 5.6, 1.2)),
    fill(P.red, ellipse(16, 17.4, 1.8, 2.4), { w: 1.2 }),
    fill(P.red, circle(9.6, 25.2, 1.3) + circle(16, 25.2, 1.3) + circle(22.4, 25.2, 1.3), { w: 1.1 }),
  ],
  /** A gold ring with a big (fake) diamond. */
  ring: [
    fill(P.gold, circle(16, 20.4, 8.8) + hole(16, 20.4, 6.4)),
    fill(P.gold, 'M12.6 12.8L13.4 10L18.6 10L19.4 12.8Z'),
    fill(P.sky, 'M10.4 7L13 3.2L19 3.2L21.6 7L16 13.4Z'),
    line('M10.6 7L21.4 7M13 3.4L14.6 7L16 13M19 3.4L17.4 7L16 13', 1.1, P.blue),
  ],
  /** A sparkling blue diamond. */
  gem: [
    flat(P.sky, 'M9 6L23 6L29 13L16 28.5L3 13Z'),
    flat(P.blue, 'M16 28.5L21.4 13L29 13Z'),
    flat(P.glass, 'M9 6L16 6L12.8 13L3 13Z'),
    line('M3 13L29 13M12.8 13L16 28.5L19.2 13M9 6L12.8 13L16 6L19.2 13L23 6', 1.1),
    line('M9 6L23 6L29 13L16 28.5L3 13Z', 1.6),
    fill(P.white, sparkle(26.8, 5.2, 2.4), { w: 1.1 }),
  ],
  /** A gold pocket watch on a chain. */
  'pocket-watch': [
    fill(
      P.gold,
      each([16.6, 4.2, 20, 3.9, 22.8, 4.6, 25, 6.3, 26.5, 9.1, 27.4, 12.4], (x, y) => circle(x, y, 1.1)),
      { w: 1.2 },
    ),
    fill(P.gold, circle(14, 5, 2.2) + hole(14, 5, 1.1)),
    fill(P.gold, rect(12.4, 6.4, 3.2, 3.4, 0.8)),
    fill(P.gold, circle(14, 19, 10.2)),
    fill(P.white, circle(14, 19, 7.8)),
    flat(INK, circle(14, 12.8, 0.8) + circle(20.2, 19, 0.8) + circle(14, 25.2, 0.8) + circle(7.8, 19, 0.8)),
    line('M14 19L14 14M14 19L17.6 21', 1.5),
  ],
  /** The tarot "Tower": a stone tower with a lightning bolt hitting the top. */
  tower: [
    fill(P.lightGray, 'M6.4 29L7.2 13L16.8 13L17.6 29Z'),
    line('M7.6 17.4H16.4M7.4 22H16.6M12 13.4V17.4M10 17.4V22M14 17.4V22', 1.2, P.gray),
    fill(
      P.lightGray,
      'M5.4 13.4L5.4 7.6L7.8 7.6L7.8 9.6L9 9.6L9 7.6L11.4 7.6L11.4 9.6L12.6 9.6L12.6 7.6L15 7.6L15 9.6L16.2 9.6L16.2 7.6L18.6 7.6L18.6 13.4Z',
    ),
    fill(P.flame, 'M10.4 20L10.4 17.4C10.4 16.3 11.1 15.6 12 15.6C12.9 15.6 13.6 16.3 13.6 17.4L13.6 20Z', {
      w: 1.2,
    }),
    fill(P.darkBrown, 'M9.8 29L9.8 25.4C9.8 24.1 10.8 23.2 12 23.2C13.2 23.2 14.2 24.1 14.2 25.4L14.2 29Z'),
    fill(
      P.fire,
      'M6.6 7.8C4.8 7.8 4.2 6.2 5 4.8C5.4 5.6 6 5.8 6.2 5.4C6 4.2 6.8 3 7.8 2.6C7.8 4 9.4 4.6 9.4 6.2C9.4 7.2 8.4 7.8 6.6 7.8Z',
    ),
    fill(P.lightGray, box(21.6, 14.6, 2.2, 2.2, 20), { w: 1.2 }),
    fill(P.lightGray, box(24.6, 18.4, 1.9, 1.9, -15), { w: 1.2 }),
    fill(P.yellow, 'M23.6 2.4L28.8 2.4L25 6.8L27.6 6.8L17.8 11.6L21.4 8.6L19.2 8.6Z'),
  ],
  /** "Decorate": a wooden painter's palette with blobs of red, yellow, blue and green paint, and a brush. */
  paint: [
    fill(
      P.tan,
      'M15 4.5C22.6 4.5 28.5 8.8 28.5 14.4C28.5 18.4 25.6 19.8 22.8 19.4C20.4 19 18.6 20.2 19.2 22.6C19.8 25.2 18 27.5 14.6 27.5C8 27.5 3.5 22.4 3.5 16C3.5 9.4 8.4 4.5 15 4.5Z' +
        circle(10.6, 21.4, 1.9),
    ),
    fill(P.red, circle(8.6, 13, 2.3), { w: 1.2 }),
    fill(P.yellow, circle(13.6, 8.8, 2.3), { w: 1.2 }),
    fill(P.blue, circle(19.8, 8.6, 2.3), { w: 1.2 }),
    fill(P.green, circle(24.6, 12.6, 2.1), { w: 1.2 }),
    ...band('M21 21.4L27.8 28', 2.2, P.red),
    ...band('M18.8 19.2L20.6 21', 3, P.silver),
    ...band('M16.4 16.8L18.4 18.8', 3, P.blue),
  ],
} satisfies Record<string, IconDef>;
